import { useT } from "@transifex/react";
import { useState } from "react";
import type { SchoolClass } from "p6m-p6u";
import IconTextRow from "../../../basic/iconTextRow/IconTextRow";
import CreateEditTextInputPopup from "../../../basic/popups/CreateEditTextInputPopup";
import { ButtonColorVariant } from "../../teacherArea/AreaButton";
import { SchoolClassVisibilities } from "../../../enums/schoolClasses";
import { MAX_NAME_LENGTH } from "../../../constants/LimitConstants";
import {
    containsUnsupportedCharacters,
    isDuplicateClassName,
    isPrivateSchoolClass,
} from "../../../utils/schoolClassUtils";
import ClassVisibilitySelector from "../ClassVisibilitySelector";
import ConsentCheckbox from "../ConsentCheckbox";

interface EditClassPopupProps {
    schoolClass: SchoolClass | null;
    onClose: () => void;
    onSubmit: (schoolClassId: string, newName: string, classVisibility?: SchoolClassVisibilities) => Promise<boolean>;
    onCleanupPrivateToPublic: (schoolClassId: string) => Promise<boolean>;
    existingClassNames: string[];
}

const EditClassPopup = (props: EditClassPopupProps) => {
    const t = useT();

    const t_editClass = t("Edit Class", { _tags: "SchoolClasses" });
    const t_className = t("Class Name", { _tags: "SchoolClasses, EditClassPopup" });
    const t_saveChanges = t("Save changes", { _tags: "SchoolClasses" });
    const t_duplicateClassWarning = t("A class with this name already exists. Tip: Add the year.", {
        _tags: "SchoolClasses",
    });
    const t_unsupportedCharacter = t(
        "The class name contains unsupported special characters. Please use only letters, numbers, and spaces.",
        { _tags: "SchoolClasses" }
    );
    const t_classVisibility = t("Class Type", { _tags: "SchoolClasses, ClassVisibilitySelector" });
    const t_consent = t(
        "I confirm that personal data of students (e.g. name and performance data) may be processed for this private class and, if applicable, linked to phase6 accounts.",
        { _tags: "SchoolClasses, EditClassPopup" }
    );
    const t_privateToPublicWarning = t('If you change your class to "Public", the following will be deleted:', {
        _tags: "SchoolClasses, EditClassPopup",
    });
    const t_warningStudents = t("All students, including their answers and results", {
        _tags: "SchoolClasses, EditClassPopup",
    });
    const t_warningTests = t("All assigned tests", {
        _tags: "SchoolClasses, EditClassPopup",
    });

    const [inputValue, setInputValue] = useState("");
    const [errorMessage, setErrorMessage] = useState("");
    const originalVisibility = props.schoolClass?.classVisibility ?? SchoolClassVisibilities.PUBLIC;

    const [selectedVisibility, setSelectedVisibility] = useState<SchoolClassVisibilities>(originalVisibility);
    const [hasConsented, setHasConsented] = useState(false);

    const isPrivate = isPrivateSchoolClass(selectedVisibility);
    const isSwitchingToPublic = isPrivateSchoolClass(originalVisibility) && !isPrivate;

    const originalName = props.schoolClass?.name ?? "";
    const trimmedInput = inputValue.trim();
    const visibilityChanged = selectedVisibility !== originalVisibility;
    const nameChanged = trimmedInput !== "" && trimmedInput !== originalName;
    const hasNoChanges = !visibilityChanged && !nameChanged;

    const handleClose = () => {
        setErrorMessage("");
        props.onClose();
    };

    const handleSubmit = async (newName: string) => {
        if (!props.schoolClass) return;

        const schoolClassId = props.schoolClass.token;
        const newVisibility = visibilityChanged ? selectedVisibility : undefined;

        if (isSwitchingToPublic) {
            props.onClose();
            const cleanupOk = await props.onCleanupPrivateToPublic(schoolClassId);
            if (!cleanupOk) return;
            await props.onSubmit(schoolClassId, newName, newVisibility);
            return;
        }

        const success = await props.onSubmit(schoolClassId, newName, newVisibility);
        if (success) {
            props.onClose();
        }
    };

    const handleVisibilityChange = (visibility: SchoolClassVisibilities) => {
        setSelectedVisibility(visibility);
        if (visibility === SchoolClassVisibilities.PUBLIC) {
            setHasConsented(false);
        }
    };

    const isConsentRequired = isPrivate && !hasConsented;

    return (
        <CreateEditTextInputPopup
            isOpen={props.schoolClass !== null}
            onClose={handleClose}
            title={t_editClass}
            label={t_className}
            initialValue={props.schoolClass?.name || ""}
            primaryButton={{
                text: t_saveChanges,
                onSubmit: handleSubmit,
                colorVariant: isSwitchingToPublic ? ButtonColorVariant.DANGER : ButtonColorVariant.DEFAULT,
                icon: isSwitchingToPublic ? "alert" : undefined,
            }}
            onInputChange={(value) => {
                setInputValue(value);
                setErrorMessage(containsUnsupportedCharacters(value) ? t_unsupportedCharacter : "");
            }}
            warningMessage={
                isDuplicateClassName({
                    name: inputValue,
                    existingNames: props.existingClassNames,
                    excludeName: props.schoolClass?.name,
                })
                    ? t_duplicateClassWarning
                    : ""
            }
            alertMessage={errorMessage}
            isDisabled={isConsentRequired || hasNoChanges}
            maxLength={MAX_NAME_LENGTH}
        >
            <div className="flex flex-col gap-xs">
                <span className="text-l text-p6-grey-dark">{t_classVisibility}</span>
                <ClassVisibilitySelector
                    selectedVisibility={selectedVisibility}
                    onVisibilityChange={handleVisibilityChange}
                />
            </div>

            {isSwitchingToPublic && (
                <div className="flex flex-col gap-xs">
                    <IconTextRow
                        data-testid="edit-class-public-warning"
                        icon="alert"
                        text={t_privateToPublicWarning}
                        iconClassName="text-xl text-p6-red"
                        textClassName="text-m text-p6-red"
                    />
                    <div className="flex flex-col gap-xs pl-m">
                        <IconTextRow
                            icon="close"
                            text={t_warningStudents}
                            iconClassName="text-xl text-p6-red"
                            textClassName="text-m text-p6-red"
                        />
                        <IconTextRow
                            icon="close"
                            text={t_warningTests}
                            iconClassName="text-xl text-p6-red"
                            textClassName="text-m text-p6-red"
                        />
                    </div>
                </div>
            )}

            {isPrivate && (
                <ConsentCheckbox
                    isChecked={hasConsented}
                    onChange={() => setHasConsented(!hasConsented)}
                    label={t_consent}
                />
            )}
        </CreateEditTextInputPopup>
    );
};

export default EditClassPopup;
